import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invalidate } from "@react-three/fiber";
import * as THREE from "three";
import { useQuery } from "@apollo/client";
import { FloorplansWithSharingDocument } from "../../../gql/floorplanCollab";
import { DeleteFloorplanBackdropImageDocument } from "../../../gql/floorplanBackdrop";
import { editorStore } from "../../../store/editorStore";
import { floorplannerStore } from "../../../store/floorplannerStore";
import { renderStore } from "../../../store/renderStore";
import {
  AreaFillSymbolType,
  BackdropImageType,
  DoorType,
  DoubleDoorType,
  Ruler3DType,
  RulerLineType,
  SingleLineType,
  BookShelfType,
  SmartTableType,
  SymbolType,
  VoidType,
  WallConnectionEnd,
  WallConnectionStart,
  WallType,
  WindowType,
  isAreaFillSymbolType,
  isAreaSymbolType,
  isBackdropImageType,
  isBookShelfType,
  isCircleStairsType,
  isCircleSymbolType,
  isDoubleDoorType,
  isDoorType,
  isSingleLeafDoorType,
  isPolygonSymbolType,
  isRectStairsType,
  isTurnStairsType,
  isRulerLineType,
  isSquareSymbolType,
  isSmartTableType,
  isSvgType,
  isTextType,
  isTriangleSymbolType,
  isVoidType,
  isWindowType,
} from "../../../types/floorplannerTypes";
import {
  normalizeSmartTableConfig,
  normalizeSmartTableHeight,
} from "../../../features/floorplan3d/symbols/dining/smartTableConfig";
import {
  normalizeBookShelfConfig,
} from "../../../features/floorplan3d/symbols/livingRoom/bookShelfConfig";
import {
  FillColorObject,
  getFillColor,
  getFillOpacity,
  getFillPatternSummary,
  getLiveSelectionObject,
  numberToHexColor,
  supportsFillColor,
  supportsFillOpacity,
  supportsPatternFill,
  resetFillOpacityForTargets,
  updateFillColorForTargets,
  updateFillOpacityForTargets,
  updateFillPatternForTargets,
} from "../../../utils/fillSelection";
import { DEFAULT_WALL_HEIGHT } from "../../../features/floorplan3d/geometry";
import { syncSymbolPlanRotationAfter3DYawChange } from "../../../features/floorplan3d/interaction/symbol3DRotationGizmo";
import {
  getRuler3DLength,
  setRuler3DDistance as setRuler3DDistanceOnObject,
} from "../../../features/floorplan3d/measurements/measurement3DGeometry";
import { updateWallConnections } from "../../FloorPlan/updateWallConnections";
import type {
  PropertyPanelController,
  PropertyPanelLocalValues,
} from "./types";
import {
  areNumbersEqual,
  degreesToRadians,
  getCommonDegreesValue,
  getCommonMillimeterValue,
  getCommonValue,
  getDisplayedObjects,
  getObjectLineColor,
  getObjectLineWeight,
  hexColorToNumber,
  toDisplayLength,
  toWorldFromDisplay,
} from "./propertyPanelUtils";
export const usePropertyPanelController = (): PropertyPanelController => {
  const [optionMenuOpen, setOptionMenuOpen] = useState(false);
  const optionMenuRef = useRef<HTMLDivElement>(null);
  const [floorplans, setFloorplans] = useState<any[]>([]);
  // Expanded by default so line/fill controls are discoverable without an extra click.
  const [linePropsOpen, setLinePropsOpen] = useState(true);
  const [fillPropsOpen, setFillPropsOpen] = useState(true);
  const [layerPropsOpen, setLayerPropsOpen] = useState(true);
  const [lightingPropsOpen, setLightingPropsOpen] = useState(true);
  const lineTypeOptions = ["solid", "dashed"];
  const [wallLength, setWallLength] = useState<number | "">(0);
  const [rulerLength, setRulerLength] = useState<number | "">(0);
  const [ruler3DDistance, setRuler3DDistance] = useState<number | "">(0);
  const [singleLineLength, setSingleLineLength] = useState<number | "">(0);
  const pendingFillColorRef = useRef<{
    targets: FillColorObject[];
    value: string;
  } | null>(null);
  const fillColorFrameRef = useRef<number | null>(null);
  const fillColorCommitTimeoutRef = useRef<number | null>(null);
  const hasPendingFillColorTransactionRef = useRef(false);
  const pendingFillOpacityRef = useRef<{
    targets: FillColorObject[];
    value: number;
  } | null>(null);
  const fillOpacityFrameRef = useRef<number | null>(null);
  const fillOpacityCommitTimeoutRef = useRef<number | null>(null);
  const hasPendingFillOpacityTransactionRef = useRef(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [backdropModal, setBackdropModal] =
    useState<PropertyPanelController["backdropModal"]>(null);
  const [localValues, setLocalValues] = useState<PropertyPanelLocalValues>({});

  const { data: sharingFloorplansData } = useQuery(FloorplansWithSharingDocument, {
    fetchPolicy: "network-only",
  });

  const deleteBackdropImage = async (backdropImage: BackdropImageType) => {
    if (floorplannerStore.lockedByOther) return;
    const imageUrl = backdropImage.backdropImagePath;
    const floorplanId = floorplannerStore.id;

    editorStore.clearSelections();
    floorplannerStore.removeSymbol(backdropImage.id);
    invalidate();

    if (!floorplanId || !imageUrl) return;

    try {
      await (floorplannerStore.client ?? editorStore.client)?.mutate({
        mutation: DeleteFloorplanBackdropImageDocument,
        variables: { floorplanId, imageUrl },
      });
    } catch (error) {
      console.warn("Could not delete floorplan backdrop image", error);
    }
  };

  useEffect(() => {
    const nextFloorplans: any[] = [];
    const source = sharingFloorplansData?.floorplans;
    if (source) {
      source.forEach((floorplan: any) => {
        nextFloorplans.push({
          id: floorplan?.id,
          name: floorplan?.name,
          isOwner: floorplan?.isOwner,
          ownerName:
            floorplan?.user?.userName || floorplan?.user?.email || null,
        });
      });
      setFloorplans(nextFloorplans);
    }
  }, [sharingFloorplansData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionMenuRef.current &&
        !optionMenuRef.current.contains(event.target as Node)
      ) {
        setOptionMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [optionMenuRef]);

  const updateWallAttachmentWidths = (
    walls: WallType[],
    valueInMillimeters: number,
  ) => {
    const wallWidth = toWorldFromDisplay(valueInMillimeters);

    walls.forEach((wall) => {
      wall.symbolAttachments?.forEach((attachment) => {
        const symbol = floorplannerStore.symbolsMap.get(
          attachment.symbolId,
        ) as SymbolType;

        if (isWindowType(symbol)) {
          const windowWidth =
            wallWidth < 0.1 ? wallWidth : floorplannerStore.windowWidth;
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "windowWidth",
            windowWidth,
          );
        }

        if (isVoidType(symbol)) {
          const voidWidth =
            wallWidth < 0.1 ? wallWidth : floorplannerStore.windowWidth;
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "voidWidth",
            voidWidth,
          );
        }
      });
    });
  };

  useEffect(() => {
    editorStore.selections.forEach((selection) => {
      if (Object.prototype.hasOwnProperty.call(selection, "type")) {
        if (selection.type === "wall") {
          const wall = floorplannerStore.wallsMap.get(selection.id);
          if (!wall) return;
          setLocalValues((prevValues) => ({
            ...prevValues,
            [selection.id]: {
              startX: toDisplayLength(wall.start.x),
              startY: toDisplayLength(wall.start.y),
              endX: toDisplayLength(wall.end.x),
              endY: toDisplayLength(wall.end.y),
              wallWidth: toDisplayLength(
                wall.wallWidth || floorplannerStore.wallWidth),
              wallHeight: toDisplayLength(
                wall.threeD?.height ?? DEFAULT_WALL_HEIGHT),
              lineWeight: wall.lineWeight ?? floorplannerStore.wallLineWeight,
              lineColor: numberToHexColor(
                wall.lineColor !== undefined && wall.lineColor !== null
                  ? wall.lineColor
                  : floorplannerStore.lineColor,
              ),
              fillColor: numberToHexColor(
                wall.fillColor !== undefined && wall.fillColor !== null
                  ? wall.fillColor
                  : floorplannerStore.fillColor,
              ),
            },
          }));
        } else if (selection.type === "singleLine") {
          const singleLine = floorplannerStore.singleLinesMap.get(selection.id);
          if (!singleLine) return;
          setLocalValues((prevValues) => ({
            ...prevValues,
            [selection.id]: {
              lineWeight:
                singleLine.lineWeight ?? floorplannerStore.singleLineWeight,
            },
          }));
        } else if (selection.type === "rulerLine") {
          const rulerLine = floorplannerStore.rulerLinesMap.get(selection.id);
          if (!rulerLine) return;
          setLocalValues((prevValues) => ({
            ...prevValues,
            [selection.id]: {
              lineWeight:
                rulerLine.lineWeight ?? floorplannerStore.rulerLineWeight,
            },
          }));
        } else if (
          [
            "symbol",
            "door",
            "slidingDoor",
            "doubleDoor",
            "window",
            "circleStairs",
            "rectStairs",
            "turnStairs",
            "square",
            "circle",
            "triangle",
            "polygon",
            "svg",
          ].includes(selection.type)
        ) {
          const symbol = floorplannerStore.symbolsMap.get(selection.id);
          if (!symbol) return;
          setLocalValues((prevValues) => ({
            ...prevValues,
            [selection.id]: {
              positionX: toDisplayLength(symbol.position.x),
              positionY: toDisplayLength(symbol.position.y),
              rotation: symbol.rotation,
              lineWidth: symbol.lineWidth,
              lineWeight:
                symbol.lineWeight ??
                (symbol.type === "window"
                  ? floorplannerStore.windowLineWeight
                  : symbol.type === "door" || symbol.type === "slidingDoor" || symbol.type === "doubleDoor"
                    ? floorplannerStore.doorLineWeight
                    : symbol.type === "circleStairs" ||
                        symbol.type === "rectStairs" ||
                        symbol.type === "turnStairs" ||
                        symbol.type === "square" ||
                        symbol.type === "circle" ||
                        symbol.type === "triangle" ||
                        symbol.type === "polygon"
                      ? floorplannerStore.symbolLineWeight
                      : symbol.type === "svg"
                        ? floorplannerStore.symbolLineWeight
                        : floorplannerStore.lineWeight),
              flipHorizontal: symbol.flipHorizontal,
              flipVertical: symbol.flipVertical,
            },
          }));
          if (isSingleLeafDoorType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                doorWidth: symbol.doorWidth,
                openAngle: symbol.openAngle,
              },
            }));
          }
          if (isDoubleDoorType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                doubleDoorWidth: symbol.doubleDoorWidth,
                openAngle: symbol.openAngle,
              },
            }));
          }
          if (isCircleStairsType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                circleStairsWidth: symbol.circleStairsWidth,
                openAngle: symbol.openAngle,
                stairStepSize: symbol.stairStepSize,
              },
            }));
          }
          if (isRectStairsType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                rectStairsWidth: symbol.rectStairsWidth,
                rectStairsHeight: symbol.rectStairsHeight,
                openAngle: symbol.openAngle,
                stairStepSize: symbol.stairStepSize,
              },
            }));
          }
          if (isTurnStairsType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                stairShape: symbol.stairShape,
                turnDirection: symbol.turnDirection,
                stairWidth: symbol.stairWidth,
                firstFlightSteps: symbol.firstFlightSteps,
                secondFlightSteps: symbol.secondFlightSteps,
                landingDepth: symbol.landingDepth,
                stairStepSize: symbol.stairStepSize,
              },
            }));
          }
          if (isSquareSymbolType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                squareWidth: symbol.squareWidth,
                squareHeight: symbol.squareHeight,
                openAngle: symbol.openAngle,
              },
            }));
          }
          if (isCircleSymbolType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                circleWidth: symbol.circleWidth,
                circleHeight: symbol.circleHeight,
                openAngle: symbol.openAngle,
              },
            }));
          }
          if (isTriangleSymbolType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                triangleWidth: symbol.triangleWidth,
                triangleHeight: symbol.triangleHeight,
                openAngle: symbol.openAngle,
              },
            }));
          }
          if (isPolygonSymbolType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                polygonWidth: symbol.polygonWidth,
                polygonHeight: symbol.polygonHeight,
                openAngle: symbol.openAngle,
              },
            }));
          }
          if (isSvgType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                svgLength: symbol.svgLength,
                svgHeight: symbol.svgHeight,
                svgPath: symbol.svgPath,
              },
            }));
          }
          if (isWindowType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                windowWidth: symbol.windowWidth,
                windowLength: symbol.windowLength,
              },
            }));
          }
          if (isVoidType(symbol)) {
            setLocalValues((prevValues) => ({
              ...prevValues,
              [selection.id]: {
                voidWidth: symbol.voidWidth,
                voidLength: symbol.voidLength,
              },
            }));
          }
        }
      }
    });
  }, [
    editorStore.selections,
    editorStore.displayLengthUnit,
    floorplannerStore.symbolsMap,
    floorplannerStore.wallsMap,
    floorplannerStore.singleLinesMap,
    floorplannerStore.rulerLinesMap,
  ]);

  const getDefaultWallMountForSymbol = (symbol: SymbolType | undefined) => {
    const walls = Array.from(floorplannerStore.wallsMap.values());
    const fallbackWall = walls[0];
    if (!symbol || !fallbackWall) {
      return {
        wallId: "",
        side: "left" as const,
        offset: 0,
        distanceFromWall: 0,
      };
    }

    let closestWall = fallbackWall;
    let closestOffset = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    walls.forEach((wall) => {
      const segment = wall.end.clone().sub(wall.start);
      const lengthSq = segment.lengthSq();
      if (lengthSq <= 1e-8) return;
      const offset = Math.max(
        0,
        Math.min(
          Math.sqrt(lengthSq),
          symbol.position
            .clone()
            .sub(wall.start)
            .dot(segment.clone().normalize()),
        ),
      );
      const closestPoint = wall.start
        .clone()
        .add(segment.clone().normalize().multiplyScalar(offset));
      const distance = closestPoint.distanceToSquared(symbol.position);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestWall = wall;
        closestOffset = offset;
      }
    });

    return {
      wallId: closestWall.id,
      side: "left" as const,
      offset: closestOffset,
      distanceFromWall: 0,
    };
  };

  const updateGlobalState = (object: any, property: string, value: any) => {
    if (floorplannerStore.lockedByOther) return;
    if (property === "fillPattern") {
      updateFillPatternForTargets(object, value);
      return;
    }

    const targetSelections = Array.isArray(object)
      ? object
      : editorStore.selections;

    targetSelections.forEach((selection) => {
      if (Object.prototype.hasOwnProperty.call(selection, "type")) {
        const liveSymbol = floorplannerStore.symbolsMap.get(selection.id);
        const isFillAreaSelection =
          isAreaSymbolType(liveSymbol ?? selection) ||
          isAreaFillSymbolType(liveSymbol ?? selection);

        if (
          isFillAreaSelection &&
          ["lineColor", "lineWeight", "lineType", "lineForm"].includes(property)
        ) {
          return;
        }

        floorplannerStore.beginUndoTransaction(`update ${property}`);
        floorplannerStore.recordObjectForUndo(selection.id);

        if (selection.type === "wall") {
          if (
            property === "lineColor" ||
            property === "fillColor" ||
            property === "lineWeight" ||
            property === "lineForm" ||
            property === "lineType"
          ) {
            const convertedValue =
              property === "lineColor" || property === "fillColor"
                ? hexColorToNumber(value)
                : value;
            floorplannerStore.setObjectProperty(
              selection.id,
              property,
              convertedValue,
            );
          } else if (property === "threeDHeight") {
            const wall = floorplannerStore.wallsMap.get(selection.id);
            floorplannerStore.setObjectProperty(selection.id, "threeD", {
              ...wall?.threeD,
              height: toWorldFromDisplay(value),
            });
          } else if (property === "length") {
            floorplannerStore.setWallLength(
              selection.id,
              toWorldFromDisplay(value),
              editorStore.whichEndToUpdate === "start"
                ? WallConnectionStart
                : editorStore.whichEndToUpdate === "end"
                  ? WallConnectionEnd
                  : undefined,
            );
            setTimeout(() => {
              floorplannerStore.objectNeedsUpdate(selection.id);
              selection.centerConnections?.forEach((connection: any) => {
                floorplannerStore.objectNeedsUpdate(connection.id);
              });
            }, 50);
            editorStore.setWallEditingInnerLength(undefined);
          } else {
            floorplannerStore.setObjectProperty(
              selection.id,
              property,
              toWorldFromDisplay(value),
            );

            if (property !== "wallWidth") {
              floorplannerStore.objectNeedsUpdate(selection.id);
              selection.centerConnections?.forEach((connection: any) => {
                if (
                  connection.sourcePosition !== WallConnectionStart &&
                  connection.sourcePosition !== WallConnectionEnd
                ) {
                  setTimeout(() => {
                    renderStore.clearWallShapes(connection.id);
                    updateWallConnections(
                      connection.id,
                      null,
                      [],
                      floorplannerStore,
                    );
                    renderStore.updateShapes(connection.id);
                    floorplannerStore.objectNeedsUpdate(connection.id);
                  }, 0);
                }
              });
            }
          }
        } else if (selection.type === "singleLine") {
          if (
            property === "lineColor" ||
            property === "lineWeight" ||
            property === "lineType" ||
            property === "lineForm" ||
            property === "controlPoint"
          ) {
            floorplannerStore.updateSingleLineProp(
              selection.id,
              property,
              value,
            );
            floorplannerStore.objectNeedsUpdate(selection.id);
          } else if (property === "length") {
            floorplannerStore.setSingleLineLength(
              selection.id,
              toWorldFromDisplay(value),
              editorStore.whichEndToUpdate,
            );
            floorplannerStore.objectNeedsUpdate(selection.id);
            editorStore.setSingleLineEditingLength(undefined);
          } else {
            floorplannerStore.updateSingleLineProp(
              selection.id,
              property as keyof SingleLineType,
              toWorldFromDisplay(value),
            );
          }
        } else if (selection.type === "rulerLine") {
          if (
            property === "lineColor" ||
            property === "lineWeight" ||
            property === "lineType"
          ) {
            floorplannerStore.updateRulerLineProp(
              selection.id,
              property,
              value,
            );
          } else if (property === "length") {
            floorplannerStore.setRulerLineLength(
              selection.id,
              toWorldFromDisplay(value),
              editorStore.whichEndToUpdate,
            );
            floorplannerStore.objectNeedsUpdate(selection.id);
            editorStore.setRulerLineEditingLength(undefined);
          } else {
            floorplannerStore.updateRulerLineProp(
              selection.id,
              property as keyof RulerLineType,
              toWorldFromDisplay(value),
            );
          }
        } else if (selection.type === "ruler3D") {
          if (property === "ruler3DDistance") {
            const ruler = floorplannerStore.rulers3DMap.get(selection.id);
            if (!ruler) return;
            const distance = Math.max(toWorldFromDisplay(value), 0.001);
            floorplannerStore.updateRuler3D(
              setRuler3DDistanceOnObject(ruler, distance),
            );
          }
        } else if (
          [
            "symbol",
            "door",
            "slidingDoor",
            "doubleDoor",
            "window",
            "circleStairs",
            "rectStairs",
            "turnStairs",
            "square",
            "circle",
            "triangle",
            "polygon",
            "svg",
            "smartTable",
            "bookShelf",
            "areaFill",
            "void",
          ].includes(selection.type)
        ) {
          if (
            property === "lineColor" ||
            property === "fillColor" ||
            property === "lineType"
          ) {
            floorplannerStore.updateSymbolProperty(
              selection.id,
              property,
              value,
            );
          } else if (property === "smartTableConfig") {
            floorplannerStore.updateSymbolProperty(
              selection.id,
              "smartTable",
              normalizeSmartTableConfig(value),
            );
          } else if (property === "smartTableHeight") {
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            const worldHeight = normalizeSmartTableHeight(
              toWorldFromDisplay(value),
            );
            floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
              ...symbol?.threeD,
              height: worldHeight,
              dimensions: {
                ...symbol?.threeD?.dimensions,
                height: worldHeight,
              },
            });
          } else if (property === "bookShelfConfig") {
            const bookShelf = normalizeBookShelfConfig(value);
            floorplannerStore.updateSymbolProperty(
              selection.id,
              "bookShelf",
              bookShelf,
            );
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
              ...symbol?.threeD,
              height: bookShelf.height,
              dimensions: {
                ...symbol?.threeD?.dimensions,
                width: bookShelf.width,
                depth: bookShelf.depth,
                height: bookShelf.height,
              },
            });
          } else if (
            property === "symbol3DHeight" ||
            property === "symbol3DBaseElevation" ||
            property === "symbol3DTopBulge"
          ) {
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            const threeDProperty =
              property === "symbol3DHeight"
                ? "height"
                : property === "symbol3DBaseElevation"
                  ? "baseElevation"
                  : "topBulge";
            const millimeterValue = Number.isFinite(value) ? value : 0;
            const worldValue = toWorldFromDisplay(millimeterValue);
            const currentThreeD = symbol?.threeD ?? {};
            const nextThreeD = {
              ...currentThreeD,
              [threeDProperty]: worldValue,
            };
            if (property === "symbol3DHeight") {
              nextThreeD.dimensions = {
                ...currentThreeD.dimensions,
                height: worldValue,
              };
              const userModel = currentThreeD.userModel;
              if (userModel) {
                nextThreeD.userModel = {
                  ...userModel,
                  dimensions: {
                    ...userModel.dimensions,
                    height: worldValue,
                  },
                };
              }
            }
            floorplannerStore.updateSymbolProperty(
              selection.id,
              "threeD",
              nextThreeD,
            );
          } else if (
            property === "doorStyle" ||
            property === "doorHandleStyle"
          ) {
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            const currentThreeD = symbol?.threeD ?? {};
            floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
              ...currentThreeD,
              [property]: value,
            });
            invalidate();
          } else if (
            property === "shape3DPlacementMode" ||
            property === "shape3DPitch" ||
            property === "shape3DRoll" ||
            property === "shape3DYaw" ||
            property === "shape3DWallSide" ||
            property === "shape3DWallOffset" ||
            property === "shape3DWallDistance"
          ) {
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            const currentThreeD = symbol?.threeD ?? {};
            if (property === "shape3DPlacementMode") {
              const placementMode =
                value === "wall" || value === "free" ? value : "floor";
              const wallMount =
                placementMode === "wall"
                  ? (currentThreeD.wallMount ??
                    getDefaultWallMountForSymbol(symbol))
                  : undefined;
              floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
                ...currentThreeD,
                placementMode,
                wallMount,
              });
            } else if (
              property === "shape3DPitch" ||
              property === "shape3DRoll" ||
              property === "shape3DYaw"
            ) {
              const degrees = parseFloat(value);
              const axis =
                property === "shape3DPitch"
                  ? "pitch"
                  : property === "shape3DRoll"
                    ? "roll"
                    : "yaw";
              const nextRotation3D = {
                ...currentThreeD.rotation3D,
                [axis]: degreesToRadians(
                  Number.isFinite(degrees) ? degrees : 0,
                ),
              };
              if (axis === "yaw") {
                const { rotation, rotation3D } =
                  syncSymbolPlanRotationAfter3DYawChange(
                    symbol?.rotation,
                    currentThreeD.rotation3D,
                    nextRotation3D,
                  );
                floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
                  ...currentThreeD,
                  rotation3D,
                });
                floorplannerStore.updateSymbolProperty(
                  selection.id,
                  "rotation",
                  rotation,
                );
              } else {
                floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
                  ...currentThreeD,
                  rotation3D: nextRotation3D,
                });
              }
            } else if (property === "shape3DWallSide") {
              floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
                ...currentThreeD,
                placementMode: "wall",
                wallMount: {
                  ...(currentThreeD.wallMount ??
                    getDefaultWallMountForSymbol(symbol)),
                  side: value === "right" ? "right" : "left",
                },
              });
            } else {
              const wallProperty =
                property === "shape3DWallOffset"
                  ? "offset"
                  : "distanceFromWall";
              const millimeterValue = parseFloat(value);
              floorplannerStore.updateSymbolProperty(selection.id, "threeD", {
                ...currentThreeD,
                placementMode: "wall",
                wallMount: {
                  ...(currentThreeD.wallMount ??
                    getDefaultWallMountForSymbol(symbol)),
                  [wallProperty]: toWorldFromDisplay(
                    Number.isFinite(millimeterValue) ? millimeterValue : 0,
                  ),
                },
              });
            }
          } else if (
            property === "length" ||
            property === "windowWidth" ||
            property === "circleStairsWidth" ||
            property === "rectStairsWidth" ||
            property === "rectStairsHeight" ||
            property === "stairWidth" ||
            property === "landingDepth" ||
            property === "squareWidth" ||
            property === "squareHeight" ||
            property === "circleWidth" ||
            property === "circleHeight" ||
            property === "circleRadius" ||
            property === "triangleWidth" ||
            property === "triangleHeight" ||
            property === "polygonWidth" ||
            property === "polygonHeight" ||
            property === "stairStepSize"
          ) {
            floorplannerStore.updateSymbolProperty(
              selection.id,
              property,
              toWorldFromDisplay(value),
            );
          } else if (property === "svgHeight" || property === "svgLength") {
            if (selection && Number.isFinite(value)) {
              floorplannerStore.updateSymbolProperty(
                selection.id,
                property,
                toWorldFromDisplay(value),
              );
            }
          } else if (property === "positionX") {
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            if (!symbol) return;
            const attachedWall = floorplannerStore.symbolIsAttached(selection.id);
            if (attachedWall) {
              floorplannerStore.recordWallForUndo(attachedWall);
            }
            floorplannerStore.updateSymbolPositionRespectingAttachment(
              selection.id,
              [toWorldFromDisplay(value), symbol.position.y],
            );
            if (attachedWall) {
              floorplannerStore.recordWallChange(attachedWall, "modify");
            }
          } else if (property === "positionY") {
            const symbol = floorplannerStore.symbolsMap.get(selection.id);
            if (!symbol) return;
            const attachedWall = floorplannerStore.symbolIsAttached(selection.id);
            if (attachedWall) {
              floorplannerStore.recordWallForUndo(attachedWall);
            }
            floorplannerStore.updateSymbolPositionRespectingAttachment(
              selection.id,
              [symbol.position.x, toWorldFromDisplay(value)],
            );
            if (attachedWall) {
              floorplannerStore.recordWallChange(attachedWall, "modify");
            }
          } else if (
            property === "windowLength" ||
            property === "doorWidth" ||
            property === "doubleDoorWidth" ||
            property === "voidLength"
          ) {
            const worldValue = toWorldFromDisplay(value);
            floorplannerStore.updateSymbolProperty(
              selection.id,
              property,
              worldValue,
            );
            const attachedWall = floorplannerStore.symbolIsAttached(
              selection.id,
            );
            if (attachedWall) {
              const selectedObject = floorplannerStore.symbolsMap.get(
                selection.id,
              ) as WindowType | DoorType | DoubleDoorType | VoidType;
              floorplannerStore.detachSymbolFromWall(selectedObject.id);
              floorplannerStore.attachSymbolToWall(selection.id, attachedWall, [
                selectedObject.position.x,
                selectedObject.position.y,
              ]);
            }
          } else {
            floorplannerStore.updateSymbolProperty(
              selection.id,
              property,
              value,
            );
          }
        }

        floorplannerStore.recordObjectChange(selection.id, "modify");
        floorplannerStore.pushToUndoStack();
      }
    });
  };

  const applyPendingFillColorPreview = useCallback(() => {
    if (!pendingFillColorRef.current) {
      return;
    }

    const { targets, value } = pendingFillColorRef.current;
    pendingFillColorRef.current = null;
    const changed = updateFillColorForTargets(targets, value, {
      commitUndo: false,
    });
    hasPendingFillColorTransactionRef.current =
      hasPendingFillColorTransactionRef.current || changed;
  }, []);

  const flushPendingFillColorUpdate = useCallback(() => {
    if (fillColorFrameRef.current !== null) {
      cancelAnimationFrame(fillColorFrameRef.current);
      fillColorFrameRef.current = null;
      applyPendingFillColorPreview();
    }

    if (fillColorCommitTimeoutRef.current !== null) {
      window.clearTimeout(fillColorCommitTimeoutRef.current);
      fillColorCommitTimeoutRef.current = null;
    }

    if (hasPendingFillColorTransactionRef.current) {
      floorplannerStore.pushToUndoStack();
      hasPendingFillColorTransactionRef.current = false;
    }
  }, [applyPendingFillColorPreview]);

  const scheduleFillColorCommit = useCallback(() => {
    if (fillColorCommitTimeoutRef.current !== null) {
      window.clearTimeout(fillColorCommitTimeoutRef.current);
    }

    fillColorCommitTimeoutRef.current = window.setTimeout(() => {
      flushPendingFillColorUpdate();
    }, 500);
  }, [flushPendingFillColorUpdate]);

  const handleFillColorChange = useCallback(
    (targets: FillColorObject[], value: string) => {
      pendingFillColorRef.current = { targets, value };

      if (fillColorFrameRef.current !== null) {
        return;
      }

      fillColorFrameRef.current = requestAnimationFrame(() => {
        fillColorFrameRef.current = null;
        applyPendingFillColorPreview();
        scheduleFillColorCommit();
      });
    },
    [applyPendingFillColorPreview, scheduleFillColorCommit],
  );

  const applyPendingFillOpacityPreview = useCallback(() => {
    if (!pendingFillOpacityRef.current) {
      return;
    }

    const { targets, value } = pendingFillOpacityRef.current;
    pendingFillOpacityRef.current = null;
    const changed = updateFillOpacityForTargets(targets, value, {
      commitUndo: false,
    });
    hasPendingFillOpacityTransactionRef.current =
      hasPendingFillOpacityTransactionRef.current || changed;
  }, []);

  const flushPendingFillOpacityUpdate = useCallback(() => {
    if (fillOpacityFrameRef.current !== null) {
      cancelAnimationFrame(fillOpacityFrameRef.current);
      fillOpacityFrameRef.current = null;
      applyPendingFillOpacityPreview();
    }

    if (fillOpacityCommitTimeoutRef.current !== null) {
      window.clearTimeout(fillOpacityCommitTimeoutRef.current);
      fillOpacityCommitTimeoutRef.current = null;
    }

    if (hasPendingFillOpacityTransactionRef.current) {
      floorplannerStore.pushToUndoStack();
      hasPendingFillOpacityTransactionRef.current = false;
    }
  }, [applyPendingFillOpacityPreview]);

  const scheduleFillOpacityCommit = useCallback(() => {
    if (fillOpacityCommitTimeoutRef.current !== null) {
      window.clearTimeout(fillOpacityCommitTimeoutRef.current);
    }

    fillOpacityCommitTimeoutRef.current = window.setTimeout(() => {
      flushPendingFillOpacityUpdate();
    }, 500);
  }, [flushPendingFillOpacityUpdate]);

  const handleFillOpacityChange = useCallback(
    (targets: FillColorObject[], value: number) => {
      pendingFillOpacityRef.current = { targets, value };

      if (fillOpacityFrameRef.current !== null) {
        return;
      }

      fillOpacityFrameRef.current = requestAnimationFrame(() => {
        fillOpacityFrameRef.current = null;
        applyPendingFillOpacityPreview();
        scheduleFillOpacityCommit();
      });
    },
    [applyPendingFillOpacityPreview, scheduleFillOpacityCommit],
  );

  const handleFillOpacityRestore = useCallback(
    (targets: FillColorObject[]) => {
      flushPendingFillOpacityUpdate();
      pendingFillOpacityRef.current = null;
      if (resetFillOpacityForTargets(targets)) {
        invalidate();
      }
    },
    [flushPendingFillOpacityUpdate],
  );

  useEffect(() => {
    return () => {
      flushPendingFillColorUpdate();
      flushPendingFillOpacityUpdate();
    };
  }, [flushPendingFillColorUpdate, flushPendingFillOpacityUpdate]);

  const fillSectionObjects = editorStore.selections
    .map((selection) => getLiveSelectionObject(selection))
    .filter(supportsFillColor);
  const firstFillSectionObject = fillSectionObjects[0];
  const sharedFillColor = getCommonValue(fillSectionObjects, (object) =>
    numberToHexColor(getFillColor(object)),
  );
  const fillOpacityObjects = fillSectionObjects.filter(supportsFillOpacity);
  const firstFillOpacityObject = fillOpacityObjects[0];
  const sharedFillOpacity = getCommonValue(fillOpacityObjects, getFillOpacity);
  const patternFillObjects = fillSectionObjects.filter(supportsPatternFill);
  const { pattern: sharedPattern, isMixed: hasMixedPatternSelection } =
    getFillPatternSummary(patternFillObjects);
  const selectedAreaFillKey = editorStore.selections
    .map((selection) => getLiveSelectionObject(selection))
    .filter((selection): selection is AreaFillSymbolType =>
      isAreaFillSymbolType(selection),
    )
    .map((selection) => selection.id)
    .sort()
    .join("|");

  useEffect(() => {
    if (selectedAreaFillKey) {
      setFillPropsOpen(true);
    }
  }, [selectedAreaFillKey]);

  useEffect(() => {
    if (editorStore.fillPropertiesOpenRequest > 0) {
      setFillPropsOpen(true);
    }
  }, [editorStore.fillPropertiesOpenRequest]);

  useEffect(() => {
    if (
      editorStore.selections.length === 1 &&
      editorStore.selections[0].type === "wall"
    ) {
      const wall = floorplannerStore.wallsMap.get(
        editorStore.selections[0].id,
      ) as WallType;
      if (wall) {
        setWallLength(
          toDisplayLength(floorplannerStore.wallLength(wall)),
        );
      }
    } else if (
      editorStore.selections.length === 1 &&
      editorStore.selections[0].type === "rulerLine"
    ) {
      const rulerLine = floorplannerStore.rulerLinesMap.get(
        editorStore.selections[0].id,
      ) as RulerLineType;
      if (rulerLine) {
        setRulerLength(
          toDisplayLength(
            floorplannerStore.rulerLineLength(rulerLine.id)),
        );
      }
    } else if (
      editorStore.selections.length === 1 &&
      editorStore.selections[0].type === "singleLine"
    ) {
      const singleLine = floorplannerStore.singleLinesMap.get(
        editorStore.selections[0].id,
      ) as SingleLineType;
      if (singleLine) {
        setSingleLineLength(
          toDisplayLength(
            floorplannerStore.singleLineLength(singleLine.id)),
        );
      }
    } else if (editorStore.selections.length === 1) {
      const ruler3D = floorplannerStore.rulers3DMap.get(
        editorStore.selections[0].id,
      );
      if (ruler3D) {
        setRuler3DDistance(toDisplayLength(getRuler3DLength(ruler3D)));
      }
    }
  }, [editorStore.selections, floorplannerStore.symbolsMap, floorplannerStore.rulers3DMap, editorStore.displayLengthUnit]);

  useEffect(() => {
    if (editorStore.wallEditingLengthPanel) {
      if (
        editorStore.selections.length === 1 &&
        editorStore.selections[0].type === "wall"
      ) {
        const wall = floorplannerStore.wallsMap.get(
          editorStore.selections[0].id,
        ) as WallType;
        if (wall) {
          const input = document.getElementById(
            "wallLength",
          ) as HTMLInputElement;
          if (input) {
            setWallLength(
              toDisplayLength(floorplannerStore.wallLength(wall)),
            );
            input.focus();
            setTimeout(() => {
              input.select();
            }, 0);
          }
        }
      }
    }
  }, [editorStore.wallEditingLengthPanel]);

  useEffect(() => {
    if (editorStore.singleLineEditingLengthPanel) {
      if (
        editorStore.selections.length === 1 &&
        editorStore.selections[0].type === "singleLine"
      ) {
        const singleLine = floorplannerStore.singleLinesMap.get(
          editorStore.selections[0].id,
        ) as SingleLineType;
        if (singleLine) {
          const input = document.getElementById(
            "singleLineLength",
          ) as HTMLInputElement;
          if (input) {
            setSingleLineLength(
              toDisplayLength(
                floorplannerStore.singleLineLength(singleLine.id)),
            );
            input.focus();
            setTimeout(() => {
              input.select();
            }, 0);
          }
        }
      }
    }
  }, [editorStore.singleLineEditingLengthPanel]);

  useEffect(() => {
    if (editorStore.rulerEditingLengthPanel) {
      if (
        editorStore.selections.length === 1 &&
        editorStore.selections[0].type === "rulerLine"
      ) {
        const rulerLine = floorplannerStore.rulerLinesMap.get(
          editorStore.selections[0].id,
        ) as RulerLineType;
        if (rulerLine) {
          const input = document.getElementById(
            "rulerLength",
          ) as HTMLInputElement;
          if (input) {
            setRulerLength(
              toDisplayLength(
                floorplannerStore.rulerLineLength(rulerLine.id)),
            );
            input.focus();
            setTimeout(() => {
              input.select();
            }, 0);
          }
        }
      }
    }
  }, [editorStore.rulerEditingLengthPanel]);

  // Map identity is stable across entry updates; these signatures force the
  // grouped selection memo to refresh when materials / fills change.
  const selectedWallContentSignature = editorStore.selections
    .filter((selection) => selection.type === "wall")
    .map((selection) => {
      const wall = floorplannerStore.wallsMap.get(selection.id);
      if (!wall) return `${selection.id}:missing`;
      return [
        wall.id,
        wall.lastUpdate ?? 0,
        wall.fillColor ?? "",
        wall.fillPattern ?? "",
        wall.fillOpacity ?? "",
        wall.threeD?.materialId ?? "",
        wall.threeD?.surfaceRef?.id ?? "",
        wall.threeD?.height ?? "",
      ].join(":");
    })
    .join("|");
  const selectedSymbolContentSignature = editorStore.selections
    .filter((selection) => selection.type !== "wall")
    .map((selection) => {
      const symbol = floorplannerStore.symbolsMap.get(selection.id);
      if (!symbol) return `${selection.id}:missing`;
      const assignmentIds = Object.entries(
        symbol.threeD?.materialAssignments ?? {},
      )
        .map(
          ([slot, assignment]) =>
            `${slot}=${assignment?.materialId ?? ""}:${assignment?.surfaceRef?.id ?? ""}`,
        )
        .join(",");
      return [
        symbol.id,
        symbol.fillColor ?? "",
        symbol.fillPattern ?? "",
        symbol.fillOpacity ?? "",
        symbol.threeD?.surfaceRef?.id ?? "",
        symbol.threeD?.fillColorOverride ? "1" : "0",
        assignmentIds,
      ].join(":");
    })
    .join("|");

  const grouped = useMemo(() => {
    const lineWeightDefaults = {
      wallLineWeight: floorplannerStore.wallLineWeight,
      singleLineWeight: floorplannerStore.singleLineWeight,
      rulerLineWeight: floorplannerStore.rulerLineWeight,
      windowLineWeight: floorplannerStore.windowLineWeight,
      doorLineWeight: floorplannerStore.doorLineWeight,
      symbolLineWeight: floorplannerStore.symbolLineWeight,
      lineWeight: floorplannerStore.lineWeight,
    };
    const selectedWalls = editorStore.selections
      .filter((selection): selection is WallType => selection.type === "wall")
      .map((selection) => floorplannerStore.wallsMap.get(selection.id))
      .filter((wall): wall is WallType => Boolean(wall));
    const displayedWalls =
      selectedWalls.length > 1 ? selectedWalls.slice(0, 1) : selectedWalls;
    const commonWallLength = getCommonValue(
      selectedWalls,
      (wall) => toDisplayLength(floorplannerStore.wallLength(wall)),
      areNumbersEqual,
    );
    const commonWallWidth = getCommonValue(
      selectedWalls,
      (wall) =>
        toDisplayLength(wall.wallWidth || floorplannerStore.wallWidth),
      areNumbersEqual,
    );
    const commonWallHeight = getCommonValue(
      selectedWalls,
      (wall) =>
        toDisplayLength(wall.threeD?.height ?? DEFAULT_WALL_HEIGHT),
      areNumbersEqual,
    );

    const selectedSingleLines = editorStore.selections
      .filter(
        (selection): selection is SingleLineType =>
          selection.type === "singleLine",
      )
      .map((selection) => floorplannerStore.singleLinesMap.get(selection.id))
      .filter((line): line is SingleLineType => Boolean(line));
    const displayedSingleLines =
      selectedSingleLines.length > 1
        ? selectedSingleLines.slice(0, 1)
        : selectedSingleLines;
    const commonSingleLineLength = getCommonValue(
      selectedSingleLines,
      (line) =>
        toDisplayLength(floorplannerStore.singleLineLength(line.id)),
      areNumbersEqual,
    );

    const selectedRulerLines = editorStore.selections
      .filter(
        (selection): selection is RulerLineType =>
          selection.type === "rulerLine",
      )
      .map((selection) => floorplannerStore.rulerLinesMap.get(selection.id))
      .filter((line): line is RulerLineType => Boolean(line));
    const displayedRulerLines =
      selectedRulerLines.length > 1
        ? selectedRulerLines.slice(0, 1)
        : selectedRulerLines;
    const commonRulerLength = getCommonValue(
      selectedRulerLines,
      (line) =>
        toDisplayLength(floorplannerStore.rulerLineLength(line.id)),
      areNumbersEqual,
    );

    const selectedRulers3D = editorStore.selections
      .map((selection) => floorplannerStore.rulers3DMap.get(selection.id))
      .filter((ruler): ruler is Ruler3DType => Boolean(ruler));
    const displayedRulers3D =
      selectedRulers3D.length > 1
        ? selectedRulers3D.slice(0, 1)
        : selectedRulers3D;
    const commonRuler3DDistance = getCommonValue(
      selectedRulers3D,
      (ruler) =>
        toDisplayLength(getRuler3DLength(ruler)),
      areNumbersEqual,
    );

    const selectedAreaFillSymbols = editorStore.selections
      .map((selection) => floorplannerStore.symbolsMap.get(selection.id))
      .filter((symbol): symbol is AreaFillSymbolType =>
        isAreaFillSymbolType(symbol),
      );
    const displayedAreaFillSymbols =
      selectedAreaFillSymbols.length > 1
        ? selectedAreaFillSymbols.slice(0, 1)
        : selectedAreaFillSymbols;
    const getSelectedSymbols = <T extends SymbolType>(
      predicate: (symbol: SymbolType | undefined) => symbol is T,
    ) =>
      editorStore.selections
        .map((selection) => floorplannerStore.symbolsMap.get(selection.id))
        .filter(predicate);

    const selectedDoors = getSelectedSymbols(isSingleLeafDoorType);
    const selectedDoubleDoors = getSelectedSymbols(isDoubleDoorType);
    const selectedWindows = getSelectedSymbols(isWindowType);
    const selectedVoids = getSelectedSymbols(isVoidType);
    const selectedCircleStairs = getSelectedSymbols(isCircleStairsType);
    const selectedRectStairs = getSelectedSymbols(isRectStairsType);
    const selectedTurnStairs = getSelectedSymbols(isTurnStairsType);
    const selectedSquares = getSelectedSymbols(isSquareSymbolType);
    const selectedCircles = getSelectedSymbols(isCircleSymbolType);
    const selectedTriangles = getSelectedSymbols(isTriangleSymbolType);
    const selectedPolygons = getSelectedSymbols(isPolygonSymbolType);
    const selectedSvgs = getSelectedSymbols(isSvgType);
    const selectedSmartTables = getSelectedSymbols<SmartTableType>(
      isSmartTableType,
    );
    const selectedBookShelves = getSelectedSymbols<BookShelfType>(
      isBookShelfType,
    );

    const lineEditableSelections = editorStore.selections.filter(
      (selection) => {
        const liveSelection = getLiveSelectionObject(selection);
        return (
          liveSelection &&
          !isTextType(liveSelection) &&
          !isBackdropImageType(liveSelection) &&
          !isVoidType(liveSelection) &&
          !isAreaSymbolType(liveSelection) &&
          !isAreaFillSymbolType(liveSelection)
        );
      },
    ) as Array<WallType | SymbolType | SingleLineType | RulerLineType>;
    const liveLineEditableSelections = lineEditableSelections
      .map((selection) => getLiveSelectionObject(selection))
      .filter(
        (
          selection,
        ): selection is
          | WallType
          | SymbolType
          | SingleLineType
          | RulerLineType => Boolean(selection),
      );
    const firstLineEditableSelection = lineEditableSelections[0];
    const firstLiveLineEditableSelection = liveLineEditableSelections[0];

    return {
      selectedWalls,
      displayedWalls,
      commonWallLength,
      commonWallWidth,
      commonWallHeight,
      selectedSingleLines,
      displayedSingleLines,
      commonSingleLineLength,
      selectedRulerLines,
      displayedRulerLines,
      commonRulerLength,
      selectedRulers3D,
      displayedRulers3D,
      commonRuler3DDistance,
      selectedAreaFillSymbols,
      displayedAreaFillSymbols,
      selectedDoors,
      displayedDoors: getDisplayedObjects(selectedDoors),
      selectedDoubleDoors,
      displayedDoubleDoors: getDisplayedObjects(selectedDoubleDoors),
      selectedWindows,
      displayedWindows: getDisplayedObjects(selectedWindows),
      selectedVoids,
      displayedVoids: getDisplayedObjects(selectedVoids),
      selectedCircleStairs,
      displayedCircleStairs: getDisplayedObjects(selectedCircleStairs),
      selectedRectStairs,
      displayedRectStairs: getDisplayedObjects(selectedRectStairs),
      selectedTurnStairs,
      displayedTurnStairs: getDisplayedObjects(selectedTurnStairs),
      selectedSquares,
      displayedSquares: getDisplayedObjects(selectedSquares),
      selectedCircles,
      displayedCircles: getDisplayedObjects(selectedCircles),
      selectedTriangles,
      displayedTriangles: getDisplayedObjects(selectedTriangles),
      selectedPolygons,
      displayedPolygons: getDisplayedObjects(selectedPolygons),
      selectedSvgs,
      displayedSvgs: getDisplayedObjects(selectedSvgs),
      selectedSmartTables,
      displayedSmartTables: getDisplayedObjects(selectedSmartTables),
      selectedBookShelves,
      displayedBookShelves: getDisplayedObjects(selectedBookShelves),
      lineEditableSelections,
      liveLineEditableSelections,
      firstLineEditableSelection,
      firstLiveLineEditableSelection,
      sharedLineColor: getCommonValue(liveLineEditableSelections, (object) =>
        getObjectLineColor(object, floorplannerStore.lineColor),
      ),
      sharedLineWeight: getCommonValue(
        liveLineEditableSelections,
        (object) => getObjectLineWeight(object, lineWeightDefaults),
        areNumbersEqual,
      ),
      sharedLineType: getCommonValue(
        liveLineEditableSelections,
        (object) => object.lineType || "solid",
      ),
      fillSectionObjects,
      firstFillSectionObject,
      sharedFillColor,
      fillOpacityObjects,
      firstFillOpacityObject,
      sharedFillOpacity,
      patternFillObjects,
      sharedPattern,
      hasMixedPatternSelection,
      isAnyTextSelected: editorStore.selections.some((object) =>
        isTextType(floorplannerStore.symbolsMap.get(object.id)),
      ),
    };
  }, [
    editorStore.selections,
    editorStore.displayLengthUnit,
    floorplannerStore.wallsMap,
    floorplannerStore.singleLinesMap,
    floorplannerStore.rulerLinesMap,
    floorplannerStore.rulers3DMap,
    floorplannerStore.symbolsMap,
    selectedWallContentSignature,
    selectedSymbolContentSignature,
    fillSectionObjects,
    firstFillSectionObject,
    sharedFillColor,
    fillOpacityObjects,
    firstFillOpacityObject,
    sharedFillOpacity,
    patternFillObjects,
    sharedPattern,
    hasMixedPatternSelection,
  ]);

  return {
    optionMenuOpen,
    setOptionMenuOpen,
    optionMenuRef,
    floorplans,
    isDeletePopupOpen,
    setIsDeletePopupOpen,
    linePropsOpen,
    setLinePropsOpen,
    fillPropsOpen,
    setFillPropsOpen,
    layerPropsOpen,
    setLayerPropsOpen,
    lightingPropsOpen,
    setLightingPropsOpen,
    lineTypeOptions,
    wallLength,
    setWallLength,
    rulerLength,
    setRulerLength,
    ruler3DDistance,
    setRuler3DDistance,
    singleLineLength,
    setSingleLineLength,
    localValues,
    setLocalValues,
    backdropModal,
    setBackdropModal,
    updateGlobalState,
    updateWallAttachmentWidths,
    handleFillColorChange,
    flushPendingFillColorUpdate,
    handleFillOpacityChange,
    flushPendingFillOpacityUpdate,
    handleFillOpacityRestore,
    deleteBackdropImage,
    grouped,
  };
};
